import * as DOMUtils from "history/DOMUtils";
import * as History from "../../../../common-deprecated/History";
import type { CarType } from "../../../shared-logic/types/CarType";
import type { CarConfigSettingsType } from "../redux/slices/CarConfigSettingsSlice";
import { minimizeBasket } from "../../../shared-logic/utils/basketUtils";
import type { BasketType } from "../../../shared-logic/types/BasketType";
import Debug from "../../../../common-deprecated/Debug";
import type { CarConfigStep } from "../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { getCarConfigStepPath } from "../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { getCommonQueryParams } from "../../../../common-deprecated/utils";

export const getQueryParams = (
    currentQuery: Record<string, unknown>,
    carConfigStep: CarConfigStep,
    car: CarType,
    basket: BasketType,
): URLSearchParams => {
    const propagatedParams = getCommonQueryParams(currentQuery);

    const params = minimizeBasket(basket, car);
    const urlQueryParams = new URLSearchParams();

    // Set the path query on the base URL
    urlQueryParams.set("path", getCarConfigStepPath(carConfigStep, car.model.id, car.id));

    // Loop through the minified basket params and dynamically add them top the URL as well
    Object.entries({ ...params, ...propagatedParams })
        // kind of a hacky solution but the value can either be a string or an empty array and i want the empty arrays filtered
        .filter(([, value]) => (Array.isArray(value) ? value.length : !!value))
        .forEach(([key, value]) => {
            urlQueryParams.set(key, String(value));
        });

    return urlQueryParams;
};

export const navigateToStep = (
    loadedStepUrls: CarConfigSettingsType["loadedStepUrls"],
    carConfigStep: CarConfigStep,
    car: CarType,
    basket: BasketType,
    isStandalone: boolean,
): void => {
    // If the SEO setup is done the stepURL should have been passed along via the injection request body
    const stepUrl = loadedStepUrls?.[carConfigStep];
    const urlParams = new URLSearchParams(DOMUtils.canUseDOM && window ? window.location.search : "");
    const currentQuery = Object.fromEntries(urlParams.entries());

    if (!isStandalone && stepUrl) {
        if (DOMUtils.canUseDOM) {
            // Wrap this whole URL building block in a try catch as the URL constructor throws an error when recieving an invalid URL.
            // This way the application does not crash and just fallbacks to the regular standalone navigation
            try {
                /**
                 * Besides updating the BaseURL using the pushState we need to also check if there have been changes to the configuration and pass these queries along
                 * How this logic works is that we check the URL for the path variable as we can presume that this is only filled in when there is a config change
                 * The path only gets added when you change from the default car or if you add a configurable(extra, pack, wheel,..)
                 */

                // Create a baseURL using the SEO friendly URL from injection body
                const seoFriendlyURL = new URL(stepUrl);

                // check the window URL for query params (NOT the commonsettings as the path query will always be filled in)
                const pathQuery = urlParams.get("path");

                // If the ?path was filled in in the URL, add the queries manually as this means something was changed in the configuration
                if (pathQuery) {
                    seoFriendlyURL.search = decodeURIComponent(
                        getQueryParams(currentQuery, carConfigStep, car, basket).toString(),
                    );
                }
                // push the window URL to the SEO optimized URL
                window.history.pushState(History.getState(), "", seoFriendlyURL);
            } catch (exception) {
                Debug.error("There was an issue formatting the SEO friendly URL", exception);

                History.pushQueryParams(
                    { path: getCarConfigStepPath(carConfigStep, car.model.id, car.id) },
                    History.HistoryType.Carconfig,
                );
            }
        }
    } else {
        /**
         * If we are on standalone or if there is no SEO setup done in AEM just redirect using the query params (This is not SEO optimized)
         * Example: ?path=configure/<modelId>/<carId>
         */
        const params = Object.fromEntries(getQueryParams(currentQuery, carConfigStep, car, basket).entries());
        History.pushQueryParams(params, History.HistoryType.Carconfig);
    }
};

export const changeCarId = (configSteps: CarConfigStep, modelId: string, carId: string): void => {
    const path = `${configSteps}/${modelId}/${carId}`;
    History.replaceQueryParams({ path });
};
